import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiClock, FiCreditCard, FiAward, FiShield, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';
import { logger } from '../../../utils/logger';

// Amounts are stored in paise — display as rupees
const rupees = (paise: number | null | undefined): string => {
  const value = Number(paise || 0) / 100;
  return '₹' + value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const dateIst = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  let h = ist.getUTCHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${pad(ist.getUTCDate())}/${pad(ist.getUTCMonth() + 1)}/${ist.getUTCFullYear()}, ${pad(h)}:${pad(ist.getUTCMinutes())} ${ampm} IST`;
};

const sectionClass = 'bg-white/5 dark:bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-white/10 p-6';
const labelClass = 'text-sm text-slate-400 dark:text-gray-400';
const valueClass = 'text-slate-800 dark:text-white font-semibold';

const MyMembership: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useCloudflareAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const api = (await import('../../../services/api')).default;
      const res = await api.getMembershipStatus();
      setData(res);
    } catch (e: any) {
      setError(e.message || 'Failed to load membership details');
      logger.error('MyMembership load failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      navigate('/login');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, authLoading, navigate]);

  if (authLoading || (loading && !data)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const status = data?.status || 'pending';
  const plan = data?.plan || null;
  const payments = data?.payments || [];
  const paidPayments = payments.filter((payment: any) => payment.status === 'paid' && payment.paymentId);
  const hasRecordedPayment = status === 'paid' && paidPayments.length > 0;

  const statusBadge = (label: string, color: string) => (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${color}`}>
      {label}
    </span>
  );

  const membershipBadge =
    status === 'paid'
      ? statusBadge('Paid Online', 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30')
      : status === 'admin_granted'
        ? statusBadge('Granted by Admin', 'bg-amber-500/15 text-amber-400 border border-amber-500/30')
        : statusBadge('Pending', 'bg-red-500/15 text-red-400 border border-red-500/30');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white">My Membership</h1>
          <p className="text-slate-500 dark:text-gray-400 mt-1">
            Your plan, payment and approval details — visible only to you.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">{error}</div>
      )}

      {/* Status overview */}
      <div className={sectionClass}>
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/10 text-blue-500">
              <FiAward className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white">
                  {plan?.name || 'No plan selected'}
                </h2>
                {membershipBadge}
              </div>
              <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
                Member Type:{' '}
                <span className="font-medium text-slate-700 dark:text-gray-200">
                  {data?.memberType || '—'}
                </span>
                {' '}&middot; Membership Status:{' '}
                <span className="font-medium text-slate-700 dark:text-gray-200 capitalize">
                  {status === 'admin_granted' ? 'Active (granted by admin)' : status === 'paid' ? 'Active (paid)' : (data?.memberStatus || 'pending')}
                </span>
              </p>
            </div>
          </div>
        </div>
        {(data?.validFrom || data?.validUntil) && (
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-6 text-sm">
            {data?.validFrom && (
              <span className="text-slate-500 dark:text-gray-400">
                Valid From:{' '}<span className={valueClass}>{dateIst(data.validFrom)}</span>
              </span>
            )}
            {data?.validUntil && (
              <span className="text-slate-500 dark:text-gray-400">
                Valid Upto:{' '}<span className={valueClass}>{dateIst(data.validUntil)}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Admin grant record */}
      {status === 'admin_granted' && (
        <div className="bg-amber-500/10 dark:bg-amber-500/5 backdrop-blur-sm rounded-2xl border border-amber-500/30 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-amber-500/15 text-amber-500 shrink-0">
              <FiShield className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-600 dark:text-amber-400">
                Granted manually by admin
              </h3>
              <p className="text-sm text-slate-600 dark:text-gray-300 mt-1">
                This membership was assigned to you directly by a CCRO administrator
                from the Admin Edit Member panel — no online payment was made for it.
              </p>
              <p className="text-sm text-slate-500 dark:text-gray-400 mt-2">
                Granted on: <span className="font-semibold text-amber-600 dark:text-amber-400">{dateIst(data.grantedAt)}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fee breakdown */}
      {plan && hasRecordedPayment ? (
        <div className={sectionClass}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-lg bg-blue-500/10 text-blue-500">
              <FiCreditCard className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Fee Breakdown</h3>
          </div>
          <div className="space-y-3 max-w-md">
            <div className="flex justify-between">
              <span className={labelClass}>Base amount ({plan.name})</span>
              <span className={valueClass}>{rupees(plan.baseAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className={labelClass}>Maintenance amount</span>
              <span className={valueClass}>{rupees(plan.maintenanceAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className={labelClass}>Gateway fee ({plan.feePercent}%)</span>
              <span className={valueClass}>{rupees(plan.gatewayFee)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-white/10">
              <span className="text-slate-700 dark:text-gray-200 font-semibold">Total</span>
              <span className="text-blue-500 dark:text-blue-400 font-bold text-lg">
                {rupees(plan.totalAmount)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        status === 'pending' && (
          <div className="bg-blue-500/10 dark:bg-blue-500/5 rounded-2xl border border-blue-500/30 p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">No membership yet</h3>
              <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
                Choose a membership plan and pay online to activate your membership.
              </p>
            </div>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
            >
              View Plans <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )
      )}

      {/* Razorpay transactions */}
      {paidPayments.length > 0 && (
        <div className={sectionClass}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-lg bg-blue-500/10 text-blue-500">
              <FiClock className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Payment History</h3>
          </div>
          <div className="space-y-3">
            {paidPayments.map((p: any) => (
              <div
                key={p.orderId}
                className="rounded-xl border border-white/10 bg-white/5 dark:bg-slate-900/40 p-4 flex flex-wrap gap-4 justify-between items-start"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      p.status === 'paid'
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : p.status === 'failed'
                          ? 'bg-red-500/15 text-red-400'
                          : 'bg-yellow-500/15 text-yellow-400'
                    }`}>
                      {p.status === 'paid' && <FiCheckCircle className="w-3.5 h-3.5" />}
                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </span>
                    <span className="text-slate-800 dark:text-white font-semibold">{rupees(p.totalAmount)}</span>
                  </div>
                  <div className="mt-2 grid gap-1 text-xs text-slate-500 dark:text-gray-400">
                    {String(p.orderId || '').startsWith('admin_') ? (
                      <span>Payment recorded by CCRO admin</span>
                    ) : (
                      <>
                        <span>Razorpay Order: <span className="font-mono">{p.orderId}</span></span>
                        {p.paymentId && <span>Razorpay Payment: <span className="font-mono">{p.paymentId}</span></span>}
                      </>
                    )}
                    <span>Initiated: {dateIst(p.createdAt)}</span>
                    {p.paidAt && <span>Paid: {dateIst(p.paidAt)}</span>
                    }
                  </div>
                </div>
                <div className="text-right text-xs text-slate-500 dark:text-gray-400">
                  <div>Base {rupees(p.baseAmount)}</div>
                  <div>Maint. {rupees(p.maintenanceAmount)}</div>
                  <div>Gateway {rupees(p.gatewayFee)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMembership;
